import { useMemo, useState } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Box, Collapse, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { TooltipCard } from '@components';
import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { CheckCircleIcon, UploadSimpleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import type { ExpectedDocument, ExpectedDocumentStatus } from '../../../utils/assessment-mapper';
import { getStandardFormatDisplay } from '../../../utils/standard-format';

/** Show the status filters only once the grid is big enough to need them. */
const FILTER_THRESHOLD = 6;
/** Rows visible before the grid starts scrolling. */
const MAX_VISIBLE_ROWS = 6;
const CARD_MIN_WIDTH = 195;
const GRID_GAP = 8;
/**
 * Fixed card height, so every card is the same size regardless of how many
 * lines its title takes. Sized to hold TITLE_MAX_LINES lines of the 12.5px /
 * 1.35 title plus the card's vertical padding and border; without it each grid
 * row sizes to its own tallest title and the rows come out ragged.
 */
const TITLE_MAX_LINES = 3;
const CARD_HEIGHT = 72;

type FilterKey = 'all' | ExpectedDocumentStatus;

interface ExpectedDocumentsDropdownProps {
  documents: ExpectedDocument[];
}

/**
 * Per-status surface, accent and icon.
 *
 * The surface encodes how complete the document is: plain white until anything
 * is uploaded, green on a full match, and amber for a partial one — the right
 * document in an accepted but non-standard format. Amber therefore means "half
 * matched", never "not started", so `missing` stays neutral.
 */
const useStatusTokens = () => {
  const theme = useTheme();
  return {
    matched: {
      surface: '#F0FDF4',
      border: theme.palette.divider,
      hoverBorder: theme.palette.success.main,
      // Matches the uploaded-document badge in AssessmentWorkflowPage exactly.
      iconColor: '#16a34a',
      footerBg: '#ECFDF5',
      footerFg: '#047857',
      dot: theme.palette.success.main,
      Icon: CheckCircleIcon,
    },
    missing: {
      surface: theme.palette.background.paper,
      border: theme.palette.divider,
      hoverBorder: theme.palette.primary.main,
      iconColor: theme.palette.text.disabled,
      footerBg: theme.palette.grey[50],
      footerFg: theme.palette.text.secondary,
      dot: theme.palette.grey[400],
      // Upload, not a document glyph — the badge says what to do next.
      Icon: UploadSimpleIcon,
    },
    wrong_format: {
      surface: '#FFFBEB',
      border: '#FDE68A',
      hoverBorder: '#D97706',
      // Same amber as the uploaded-document warning badge.
      iconColor: '#F59E0B',
      footerBg: '#FEF6EC',
      footerFg: '#92400E',
      dot: '#D97706',
      Icon: WarningCircleIcon,
    },
  } as const;
};

export const ExpectedDocumentsDropdown = observer(({ documents }: ExpectedDocumentsDropdownProps) => {
  const theme = useTheme();
  const { t } = useTrans();
  const { dir } = useDirection();
  // Open by default — the expected documents are the point of the step, so they
  // should not need a click to be seen.
  const [isDocsExpanded, setIsDocsExpanded] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('all');
  const statusTokens = useStatusTokens();

  const counts = useMemo(() => ({
    matched: documents.filter((d) => d.status === 'matched').length,
    wrong_format: documents.filter((d) => d.status === 'wrong_format').length,
    missing: documents.filter((d) => d.status === 'missing').length,
  }), [documents]);

  const visible = useMemo(
    () => (filter === 'all' ? documents : documents.filter((d) => d.status === filter)),
    [documents, filter],
  );

  if (documents.length === 0) return null;

  // A wrong-format document still matched its standard document, so it counts
  // toward "done" — it is surfaced as a warning, not an outstanding upload.
  const doneCount = counts.matched + counts.wrong_format;
  const allMatched = doneCount === documents.length;
  const showFilters = documents.length > FILTER_THRESHOLD;

  const filterChips: { key: FilterKey; label: string; count: number; dot?: string }[] = [
    { key: 'all', label: t('assessment.workflow.domain.filterAll'), count: documents.length },
    ...(counts.wrong_format > 0
      ? [{
        key: 'wrong_format' as const,
        label: t('assessment.workflow.domain.wrongFormat'),
        count: counts.wrong_format,
        dot: statusTokens.wrong_format.dot,
      }]
      : []),
    { key: 'missing', label: t('assessment.workflow.domain.filterMissing'), count: counts.missing, dot: statusTokens.missing.dot },
    { key: 'matched', label: t('assessment.workflow.domain.filterMatched'), count: counts.matched, dot: statusTokens.matched.dot },
  ];

  return (
    <NoFlipBox mode={dir} sx={{ mt: 1 }}>
      <Box
        onClick={() => setIsDocsExpanded(!isDocsExpanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          p: 1.5,
          position: 'relative',
          maxWidth: '40%',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
            transition: 'all 0.2s ease',
          },
          '&:hover::after': {
            background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.success.dark})`,
          },
          transition: 'all 0.2s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="textSM_medium" sx={{ color: 'text.primary' }}>
            {t('assessment.workflow.domain.expectedDocuments')}
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.125,
              borderRadius: 999,
              backgroundColor: allMatched ? '#D1FAE5' : 'grey.100',
              color: allMatched ? '#065F46' : 'text.secondary',
              fontSize: 11,
              fontWeight: 500,
              lineHeight: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            {t('assessment.workflow.domain.documentsDone', {
              matched: doneCount,
              total: documents.length,
            })}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            transform: isDocsExpanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            color: 'primary.main',
          }}
        >
          <ExpandMoreIcon />
        </Box>
      </Box>

      <Collapse in={isDocsExpanded}>
        {showFilters && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            {filterChips.map((chip) => {
              const active = filter === chip.key;
              return (
                <Box
                  key={chip.key}
                  component="button"
                  type="button"
                  onClick={() => setFilter(chip.key)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.875,
                    height: 30,
                    px: 1.375,
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: active ? 'text.primary' : 'divider',
                    backgroundColor: active ? 'text.primary' : 'background.paper',
                    color: active ? 'background.paper' : 'text.secondary',
                    fontSize: 12.5,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background-color 150ms ease, border-color 150ms ease',
                    '&:hover': { backgroundColor: active ? 'text.primary' : 'grey.100' },
                  }}
                >
                  {chip.dot && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: chip.dot, flexShrink: 0 }} />
                  )}
                  {chip.label}
                  <Box
                    sx={{
                      display: 'inline-grid',
                      placeItems: 'center',
                      minWidth: 18,
                      height: 18,
                      px: 0.625,
                      borderRadius: 999,
                      backgroundColor: active ? 'rgba(255,255,255,0.18)' : 'grey.200',
                      color: active ? 'background.paper' : 'text.secondary',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {chip.count}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
            gridAutoRows: `${CARD_HEIGHT}px`,
            gap: `${GRID_GAP}px`,
            alignItems: 'stretch',
            mt: 1.5,
            // Cap at MAX_VISIBLE_ROWS and scroll past it, so a 20-document
            // domain doesn't push the upload area off-screen.
            maxHeight: MAX_VISIBLE_ROWS * CARD_HEIGHT + (MAX_VISIBLE_ROWS - 1) * GRID_GAP,
            overflowY: 'auto',
            pr: 0.5,
          }}
        >
          {visible.map((doc, idx) => {
            const tokens = statusTokens[doc.status];
            const StatusIcon = tokens.Icon;
            const format = getStandardFormatDisplay(doc.standardFormat);
            const FormatIcon = format?.Icon;
            const isWrongFormat = doc.status === 'wrong_format';
            const uploadedExtension = isWrongFormat
              ? (/\.([^.\\/]+)$/.exec(doc.matchedFileName ?? '')?.[1] ?? '').toUpperCase()
              : '';

            // Title → description → formats → status footer, per the design.
            const tooltipBody = (
              // The tooltip is portaled to document.body, outside this block's
              // NoFlip wrapper, so it inherits no direction of its own — it needs
              // its own pin.
              <NoFlipBox
                mode={dir}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                {doc.description && (
                  <Typography
                    component="p"
                    sx={{ fontSize: 12.5, lineHeight: 1.55, color: 'text.secondary', m: 0, mb: 1.5 }}
                  >
                    {doc.description}
                  </Typography>
                )}

                {format && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      pb: 1.5,
                      // Full-bleed alert strip when the upload's format is off.
                      ...(isWrongFormat && {
                        mx: -1.875,
                        px: 1.875,
                        py: 1.125,
                        mb: 0,
                        backgroundColor: '#FFFBEB',
                        borderTop: '1px solid #FDE68A',
                        borderBottom: '1px solid #FDE68A',
                      }),
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                      }}
                    >
                      {t('assessment.workflow.domain.standardFormat')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {isWrongFormat && uploadedExtension && (
                        <Box sx={{ ...chipSx, color: '#92400E', backgroundColor: '#FEF3C7', borderColor: '#FDE68A', textDecoration: 'line-through' }}>
                          {uploadedExtension}
                        </Box>
                      )}
                      <Box sx={{ ...chipSx, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {FormatIcon && <FormatIcon size={11} weight="duotone" />}
                        {format.label}
                      </Box>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.875,
                    fontSize: 12,
                    px: 1.875,
                    py: 1.125,
                    mx: -1.875,
                    mb: -1.5,
                    backgroundColor: tokens.footerBg,
                    color: tokens.footerFg,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderRadius: '0 0 12px 12px',
                  }}
                >
                  <StatusIcon size={15} weight="fill" />
                  <Box component="span">
                    {doc.status === 'matched' && doc.matchedFileName}
                    {doc.status === 'missing' && (doc.required
                      ? t('assessment.workflow.domain.requiredNotUploaded')
                      : t('assessment.workflow.domain.optionalNotUploaded'))}
                    {isWrongFormat && t('assessment.workflow.domain.wrongFormatHint', {
                      uploaded: uploadedExtension || '—',
                      expected: format?.label ?? '',
                    })}
                  </Box>
                </Box>
              </NoFlipBox>
            );

            return (
              <TooltipCard
                key={idx}
                variant="default"
                placement="top"
                title={doc.name}
                description={tooltipBody}
                maxWidth={340}
                sx={{ p: 1.875, borderRadius: '12px' }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    // Drop the icon gutter when there is no format glyph, so
                    // titles start flush at the card edge instead of indented.
                    gridTemplateColumns: FormatIcon ? '18px 1fr auto' : '1fr auto',
                    alignItems: 'start',
                    gap: 1.125,
                    p: '10px 11px',
                    height: '100%',
                    border: '1px solid',
                    borderColor: tokens.border,
                    borderRadius: '8px',
                    backgroundColor: tokens.surface,
                    cursor: 'default',
                    minWidth: 0,
                    transition: 'border-color 140ms ease, box-shadow 140ms ease',
                    '&:hover': {
                      borderColor: tokens.hoverBorder,
                      boxShadow: '0 2px 8px rgba(17,24,39,.07)',
                    },
                  }}
                >
                  {/* Leading glyph is the document's standard format, so the
                      format reads off the card without opening the tooltip.
                      Omitted entirely when the catalog carries no standard
                      format — a generic placeholder icon says nothing and just
                      indents every title. */}
                  {FormatIcon && (
                    <Box
                      sx={{ color: `${format?.paletteKey}.main`, display: 'flex', mt: '1px' }}
                      aria-label={format?.label}
                    >
                      <FormatIcon size={18} weight="duotone" />
                    </Box>
                  )}

                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.35,
                      overflowWrap: 'anywhere',
                      minWidth: 0,
                      // Clamp so an unusually long name can't outgrow the fixed
                      // card height; the tooltip title always carries it in full.
                      display: '-webkit-box',
                      WebkitLineClamp: TITLE_MAX_LINES,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {doc.name}
                  </Typography>

                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: '1px' }}>
                    {isWrongFormat && uploadedExtension && (
                      <Box
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          color: '#92400E',
                          backgroundColor: '#FEF3C7',
                          px: 0.625,
                          py: '2px',
                          borderRadius: '4px',
                          letterSpacing: '.02em',
                        }}
                      >
                        .{uploadedExtension.toLowerCase()}
                      </Box>
                    )}
                    {!doc.required && (
                      <Box
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          letterSpacing: '.04em',
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          backgroundColor: 'grey.100',
                          px: 0.625,
                          py: '2px',
                          borderRadius: '4px',
                        }}
                      >
                        {t('assessment.workflow.domain.optionalShort')}
                      </Box>
                    )}
                    {/* Same mark and colour as the uploaded-document badge, so
                        an expected document and the file that satisfies it read
                        as the same state on both sides of the screen. */}
                    <Box sx={{ display: 'flex', flexShrink: 0, color: tokens.iconColor }}>
                      <StatusIcon size={18} weight="fill" />
                    </Box>
                  </Box>
                </Box>
              </TooltipCard>
            );
          })}
        </Box>
      </Collapse>

      <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />
    </NoFlipBox>
  );
});

/** Shared look for the small format pills inside the tooltip. */
const chipSx = {
  fontSize: 10.5,
  fontWeight: 600,
  color: 'text.secondary',
  backgroundColor: 'grey.100',
  border: '1px solid',
  borderColor: 'divider',
  px: 0.875,
  py: '2px',
  borderRadius: '5px',
  letterSpacing: '.02em',
} as const;
